import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { getBlogArticles } from '@/lib/api';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';
import { Calendar, ArrowRight, ExternalLink, Loader2 } from 'lucide-react';

export default function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState({});

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await getBlogArticles();
        setArticles(response.data);
        
        // Preload all images immediately
        response.data.forEach((article, index) => {
          const img = new Image();
          img.src = article.image;
          img.onload = () => {
            setImagesLoaded(prev => ({ ...prev, [index]: true }));
          };
        });
      } catch (error) {
        console.error('Error fetching blog articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  return (
    <PublicLayout>
      <SEO
        title="Blog — Inspirations Décoration & Art de Vivre"
        description="Explorez nos articles de blog : tendances déco, inspirations bohèmes, conseils d'aménagement et histoires autour de l'artisanat."
        canonical="/blog"
      />
      {/* Header */}
      <section className="py-16 px-6 lg:px-12 bg-muted/30 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-semibold mb-4">
              Notre Blog
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Découvrez nos conseils, inspirations et actualités sur l'univers des tapis 
              et de la décoration intérieure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">Chargement des articles...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group"
                >
                  <a 
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    data-testid={`blog-article-${article.id}`}
                  >
                    {/* Image with eager loading */}
                    <div className="aspect-[4/3] overflow-hidden rounded-lg mb-4 bg-muted relative">
                      {!imagesLoaded[index] && (
                        <div className="absolute inset-0 flex items-center justify-center bg-muted">
                          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        </div>
                      )}
                      <img
                        src={article.image}
                        alt={article.title}
                        loading="eager"
                        decoding="async"
                        fetchPriority={index < 3 ? "high" : "auto"}
                        className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
                          imagesLoaded[index] ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => setImagesLoaded(prev => ({ ...prev, [index]: true }))}
                      />
                    </div>

                    {/* Content */}
                    <div className="space-y-3">
                      {/* Date */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>{article.date}</span>
                      </div>

                      {/* Title */}
                      <h2 className="font-heading text-xl font-semibold leading-tight group-hover:text-primary transition-colors duration-300 line-clamp-2">
                        {article.title}
                      </h2>

                      {/* Excerpt */}
                      <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                        {article.excerpt}
                      </p>

                      {/* Read more */}
                      <div className="flex items-center gap-2 text-primary font-medium text-sm pt-2">
                        <span>Lire l'article</span>
                        <ExternalLink className="w-4 h-4" />
                      </div>
                    </div>
                  </a>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 lg:px-12 bg-muted/30 border-t border-border">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl font-semibold mb-4">
            Envie de découvrir nos collections ?
          </h2>
          <p className="text-muted-foreground mb-8">
            Visitez notre boutique en ligne pour trouver le tapis parfait pour votre intérieur.
          </p>
          <Link
            to="/commander-en-ligne"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="blog-cta-catalogue"
          >
            Commander en ligne
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
